import { Accessor, onCleanup } from "solid-js";

/**
 * Configuration for the swipe directive
 */
export type SwipeConfig = {
  /**
   * Called when the user swipes from left to right
   */
  onSwipeRight?: () => void;

  /**
   * Called when the user swipes from right to left
   */
  onSwipeLeft?: () => void;
};

/**
 * Distance in pixels required before a swipe is recognised
 */
const SWIPE_THRESHOLD = 70;

/**
 * Distance of vertical movement before we assume the user is scrolling
 */
const VERTICAL_CANCEL = 24;

/**
 * Distance of horizontal movement before we lock onto the gesture
 */
const HORIZONTAL_LOCK = 10;

/**
 * Maximum distance (in px) the element can be translated
 */
const MAX_TRANSLATE = 140;

/**
 * Damping factor applied to the finger movement for a softer feel
 */
const DAMPING = 0.6;

/**
 * Detect horizontal swipe gestures on a touch device.
 *
 * Only horizontal swipes are handled; vertical scrolling is left to the
 * browser. The element is translated horizontally (with damping) to give
 * visual feedback while the user drags, then springs back on release.
 */
export function swipe(
  element: HTMLElement,
  accessor: Accessor<SwipeConfig | undefined>,
) {
  let startX = 0;
  let startY = 0;
  let tracking = false;
  let horizontal = false;

  function reset() {
    element.style.transition = "";
    element.style.transform = "";
  }

  function onTouchStart(e: TouchEvent) {
    if (e.touches.length !== 1) {
      tracking = false;
      return;
    }

    // Keep this gesture scoped to the message: don't let the app's
    // slide-out drawer (which listens on the root) also react to it.
    e.stopPropagation();

    const touch = e.touches[0];
    startX = touch.clientX;
    startY = touch.clientY;
    tracking = true;
    horizontal = false;
  }

  function onTouchMove(e: TouchEvent) {
    if (!tracking) return;

    const touch = e.touches[0];
    const dx = touch.clientX - startX;
    const dy = touch.clientY - startY;

    if (!horizontal) {
      // If the user is clearly scrolling vertically, hand back to the browser
      if (Math.abs(dy) > VERTICAL_CANCEL && Math.abs(dy) > Math.abs(dx)) {
        tracking = false;
        reset();
        return;
      }

      // Only become a swipe once horizontal movement dominates
      if (Math.abs(dx) > HORIZONTAL_LOCK && Math.abs(dx) > Math.abs(dy)) {
        horizontal = true;
      }
    }

    if (horizontal) {
      e.stopPropagation();

      // Damped + clamped translation for a smoother, more controlled feel
      const clamped = Math.max(-MAX_TRANSLATE, Math.min(MAX_TRANSLATE, dx));
      const damped = clamped * DAMPING;

      element.style.transition = "none";
      element.style.transform = `translateX(${damped}px)`;
    }
  }

  function onTouchEnd(e: TouchEvent) {
    if (!tracking) return;
    tracking = false;

    const touch = e.changedTouches[0];
    if (!touch) {
      reset();
      return;
    }

    const dx = touch.clientX - startX;
    const dy = touch.clientY - startY;

    // Spring back into place
    element.style.transition = "transform 220ms cubic-bezier(0.22, 1, 0.36, 1)";
    element.style.transform = "";

    if (!horizontal) return;
    if (Math.abs(dx) < SWIPE_THRESHOLD) return;
    if (Math.abs(dx) <= Math.abs(dy)) return;

    const config = accessor();
    if (dx > 0) {
      config?.onSwipeRight?.();
    } else {
      config?.onSwipeLeft?.();
    }
  }

  // Only pan vertically with the browser; horizontal is handled here.
  element.style.touchAction = "pan-y";

  element.addEventListener("touchstart", onTouchStart, { passive: true });
  element.addEventListener("touchmove", onTouchMove, { passive: true });
  element.addEventListener("touchend", onTouchEnd);
  element.addEventListener("touchcancel", onTouchEnd);

  onCleanup(() => {
    element.style.touchAction = "";
    element.removeEventListener("touchstart", onTouchStart);
    element.removeEventListener("touchmove", onTouchMove);
    element.removeEventListener("touchend", onTouchEnd);
    element.removeEventListener("touchcancel", onTouchEnd);
    reset();
  });
}
